import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Bible, TimeSelection } from "./domain/types";
import { EMPTY_WORLD, type WorldData } from "./domain/world";
import { diffBibles } from "./domain/diff";
import { validateBible } from "./domain/validate";
import * as remote from "./persistence/api";
import { idbGet, idbSet } from "./persistence/idb";

type SyncState = "idle" | "loading" | "saving" | "offline" | "conflict" | "error";

type Store = {
  bible: Bible | null;
  world: WorldData;
  revisionNumber: number;
  updatedAt: string | null;
  selection: TimeSelection;
  dirty: boolean;
  online: boolean;
  syncState: SyncState;
  error: string | null;
  conflictRevision: number | null;
  setSelection: (patch: Partial<TimeSelection>) => void;
  updateBible: (mutator: (draft: Bible) => Bible, summary: string) => void;
  save: () => Promise<void>;
  undo: () => void;
  reloadRemote: () => Promise<void>;
  resetToSeed: () => Promise<void>;
};

const Ctx = createContext<Store | null>(null);

const defaultSelection: TimeSelection = {
  bookId: null,
  momentId: null,
  orderMode: "reading",
  viewer: "author",
};

export function StoreProvider({ children }: { children: ReactNode }) {
  const [bible, setBible] = useState<Bible | null>(null);
  const [world, setWorld] = useState<WorldData>(EMPTY_WORLD);
  const [revisionNumber, setRevisionNumber] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [selection, setSelectionState] = useState<TimeSelection>(defaultSelection);
  const [dirty, setDirty] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [syncState, setSyncState] = useState<SyncState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [conflictRevision, setConflictRevision] = useState<number | null>(null);
  const [undoStack, setUndoStack] = useState<Bible[]>([]);
  const [lastSummary, setLastSummary] = useState("Modification locale");

  const persistDraft = useCallback(async (next: Bible, rev: number) => {
    await idbSet("draft", { bible: next, revisionNumber: rev, savedAt: new Date().toISOString() });
  }, []);

  const load = useCallback(async () => {
    setSyncState("loading");
    try {
      const draft = await idbGet<{ bible: Bible; revisionNumber: number }>("draft");
      const remoteState = await remote.fetchBible();
      setWorld(remoteState.world ?? EMPTY_WORLD);
      setUpdatedAt(remoteState.updatedAt);
      if (draft && draft.revisionNumber === remoteState.revisionNumber) {
        const same = JSON.stringify(draft.bible) === JSON.stringify(remoteState.bible);
        setBible(same ? remoteState.bible : draft.bible);
        setRevisionNumber(remoteState.revisionNumber);
        setDirty(!same);
      } else if (draft && draft.revisionNumber !== remoteState.revisionNumber) {
        setBible(remoteState.bible);
        setRevisionNumber(remoteState.revisionNumber);
        setDirty(false);
        setError("Une version distante plus récente a remplacé le brouillon local. Exportez-le depuis IndexedDB si besoin.");
      } else {
        setBible(remoteState.bible);
        setRevisionNumber(remoteState.revisionNumber);
        setDirty(false);
      }
      setSyncState(navigator.onLine ? "idle" : "offline");
    } catch (e) {
      const draft = await idbGet<{ bible: Bible; revisionNumber: number }>("draft");
      if (draft?.bible) {
        setBible(draft.bible);
        setRevisionNumber(draft.revisionNumber);
        setDirty(true);
        setSyncState("offline");
        setError("Serveur injoignable — brouillon local chargé.");
        return;
      }
      setSyncState("error");
      setError(e instanceof Error ? e.message : "Chargement impossible.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => {
      setOnline(false);
      setSyncState("offline");
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const setSelection = useCallback((patch: Partial<TimeSelection>) => {
    setSelectionState((prev) => ({ ...prev, ...patch }));
  }, []);

  const updateBible = useCallback(
    (mutator: (draft: Bible) => Bible, summary: string) => {
      setBible((prev) => {
        if (!prev) return prev;
        const next = mutator(structuredClone(prev));
        next.meta = { ...next.meta, updatedAt: new Date().toISOString() };
        setUndoStack((stack) => [...stack.slice(-29), prev]);
        setDirty(true);
        setLastSummary(summary);
        void persistDraft(next, revisionNumber);
        return next;
      });
    },
    [persistDraft, revisionNumber],
  );

  const save = useCallback(async () => {
    if (!bible) return;
    const errors = validateBible(bible);
    if (errors.length) {
      setError(errors.map((e) => `${e.path}: ${e.message}`).join(" · "));
      setSyncState("error");
      return;
    }
    if (!navigator.onLine) {
      setSyncState("offline");
      setError("Hors ligne : la modification reste en brouillon local. Exportez une copie de secours.");
      await persistDraft(bible, revisionNumber);
      return;
    }
    setSyncState("saving");
    setError(null);
    try {
      const ops = undoStack.length ? diffBibles(undoStack[undoStack.length - 1]!, bible) : [];
      const result = await remote.saveBible({
        bible,
        world,
        baseRevisionNumber: revisionNumber,
        summary: lastSummary,
        operations: ops,
      });
      setBible(result.bible);
      setWorld(result.world ?? world);
      setRevisionNumber(result.revisionNumber);
      setUpdatedAt(result.updatedAt);
      setDirty(false);
      setConflictRevision(null);
      setSyncState("idle");
      await persistDraft(result.bible, result.revisionNumber);
    } catch (e) {
      const payload = (e as { payload?: remote.RemoteState; status?: number }).payload;
      if (payload?.conflict) {
        setSyncState("conflict");
        setConflictRevision(payload.currentRevision ?? null);
        setError("La version distante a changé. Rechargez, fusionnez ou exportez votre copie locale.");
        return;
      }
      setSyncState("error");
      setError(e instanceof Error ? e.message : "Sauvegarde impossible.");
    }
  }, [bible, lastSummary, persistDraft, revisionNumber, undoStack, world]);

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (!stack.length || !bible) return stack;
      const prev = stack[stack.length - 1]!;
      setBible(prev);
      setDirty(true);
      void persistDraft(prev, revisionNumber);
      return stack.slice(0, -1);
    });
  }, [bible, persistDraft, revisionNumber]);

  const reloadRemote = useCallback(async () => {
    await load();
    setDirty(false);
    setConflictRevision(null);
  }, [load]);

  const resetToSeed = useCallback(async () => {
    const result = await remote.resetSeed();
    setBible(result.bible);
    setWorld(result.world ?? EMPTY_WORLD);
    setRevisionNumber(result.revisionNumber);
    setUpdatedAt(result.updatedAt);
    setDirty(false);
    setUndoStack([]);
    await persistDraft(result.bible, result.revisionNumber);
  }, [persistDraft]);

  const value = useMemo<Store>(
    () => ({
      bible,
      world,
      revisionNumber,
      updatedAt,
      selection,
      dirty,
      online,
      syncState,
      error,
      conflictRevision,
      setSelection,
      updateBible,
      save,
      undo,
      reloadRemote,
      resetToSeed,
    }),
    [
      bible,
      conflictRevision,
      dirty,
      error,
      online,
      reloadRemote,
      resetToSeed,
      revisionNumber,
      save,
      selection,
      setSelection,
      syncState,
      undo,
      updateBible,
      updatedAt,
      world,
    ],
  );

  return createElement(Ctx.Provider, { value }, children);
}

export function useStore(): Store {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStore hors StoreProvider");
  return ctx;
}
